
import type { ReactNode } from 'react';
import { ALLPOWERS_CODE_EXAMPLES } from '@/lib/allpowers-data';
import { type PowerInfo } from '@/lib/powers-data';
import { AllpowersRequiredList } from '@/components/help-center/powers/AllpowersRequiredList';
import { CopyCodeButton } from '@/components/help-center/powers/PowerEverypowerSection';
import { cn } from '@/lib/utils';
import { Egg, Rocket, Sparkles, Code2, type LucideIcon } from 'lucide-react';

interface PowerAllpowersSectionProps {
  power: PowerInfo;
}

type FeatureCard = {
  title: string;
  description: ReactNode;
  icon: LucideIcon;
  accent: {
    ring: string;
    iconBg: string;
    iconText: string;
    cardBorder: string;
    cardBg: string;
    watermark: string;
  };
};

const FEATURE_CARDS: FeatureCard[] = [
  {
    title: 'Golden Egg',
    description: (
      <>
        Chapéu exclusivo com{' '}
        <code className="rounded-md bg-white/90 px-1.5 py-0.5 font-mono text-[11px] text-yellow-900 ring-1 ring-yellow-200/80">
          (hat#e)
        </code>
        . Para colorir use{' '}
        <code className="rounded-md bg-white/90 px-1.5 py-0.5 font-mono text-[11px] text-yellow-900 ring-1 ring-yellow-200/80">
          (hat#e#FFD800)
        </code>{' '}
        ou{' '}
        <code className="rounded-md bg-white/90 px-1.5 py-0.5 font-mono text-[11px] text-yellow-900 ring-1 ring-yellow-200/80">
          (hat#g#FFD800)
        </code>{' '}
        sem gifts.
      </>
    ),
    icon: Egg,
    accent: {
      ring: 'ring-yellow-200/70',
      iconBg: 'bg-gradient-to-br from-yellow-400 to-amber-500',
      iconText: 'text-white',
      cardBorder: 'border-yellow-200/80',
      cardBg: 'from-yellow-50/90 via-white to-amber-50/30',
      watermark: 'text-yellow-100',
    },
  },
  {
    title: 'Efeito rocket',
    description: (
      <>
        Adicione{' '}
        <code className="rounded-md bg-white/90 px-1.5 py-0.5 font-mono text-[11px] text-orange-900 ring-1 ring-orange-200/80">
          #allpowers
        </code>{' '}
        a qualquer smiley — por exemplo{' '}
        <code className="rounded-md bg-white/90 px-1.5 py-0.5 font-mono text-[11px] text-orange-900 ring-1 ring-orange-200/80">
          (smile#allpowers)
        </code>
        .
      </>
    ),
    icon: Rocket,
    accent: {
      ring: 'ring-orange-200/70',
      iconBg: 'bg-gradient-to-br from-orange-400 to-amber-600',
      iconText: 'text-white',
      cardBorder: 'border-orange-200/80',
      cardBg: 'from-orange-50/90 via-white to-amber-50/20',
      watermark: 'text-orange-100',
    },
  },
  {
    title: 'Efeito anel',
    description: (
      <>
        Use{' '}
        <code className="rounded-md bg-white/90 px-1.5 py-0.5 font-mono text-[11px] text-rose-900 ring-1 ring-rose-200/80">
          #allpowers#w1
        </code>{' '}
        (ou outro carácter após{' '}
        <code className="rounded-md bg-white/90 px-1.5 py-0.5 font-mono text-[11px] text-rose-900 ring-1 ring-rose-200/80">
          #w
        </code>
        ) para trocar o rocket por um anel.
      </>
    ),
    icon: Sparkles,
    accent: {
      ring: 'ring-rose-200/70',
      iconBg: 'bg-gradient-to-br from-rose-400 to-pink-500',
      iconText: 'text-white',
      cardBorder: 'border-rose-200/80',
      cardBg: 'from-rose-50/90 via-white to-pink-50/20',
      watermark: 'text-rose-100',
    },
  },
];

export default function PowerAllpowersSection({ power }: PowerAllpowersSectionProps) {
  if (power.slug !== 'allpowers') return null;

  return (
    <section className="border-b border-gray-100 bg-gradient-to-b from-amber-50/35 via-white to-white py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 ring-1 ring-amber-200/80">
              <Sparkles className="h-5 w-5 text-amber-700" aria-hidden />
            </div>
            <div>
              <p className="font-poppins text-xs font-semibold uppercase tracking-widest text-amber-700">
                Funcionalidade especial
              </p>
              <h2 className="font-poppins text-2xl font-bold text-gray-900 md:text-3xl">AllPowers</h2>
            </div>
          </div>

          <div className="space-y-3 font-inter text-sm leading-relaxed text-gray-600 md:text-base">
            <p>
              AllPowers <strong className="font-semibold text-gray-900">não é um power</strong>{' '}
              comprável — é uma conquista desbloqueada quando você possui{' '}
              <strong className="font-semibold text-gray-900">
                pelo menos um exemplar de cada power
              </strong>{' '}
              do conjunto oficial.
            </p>
            <p>
              Novos powers podem entrar no conjunto sem aviso. Consulte a lista abaixo para ver o
              que ainda falta completar a sua coleção.
            </p>
          </div>
        </div>

        <div className="mb-12 grid gap-5 lg:grid-cols-3">
          {FEATURE_CARDS.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <article
                key={feature.title}
                className={cn(
                  'group relative overflow-hidden rounded-2xl border bg-gradient-to-br p-6 shadow-sm transition-all duration-300',
                  'hover:-translate-y-1 hover:shadow-lg hover:shadow-amber-100/50',
                  feature.accent.cardBorder,
                  feature.accent.cardBg
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none absolute -right-2 -top-4 font-poppins text-7xl font-extrabold leading-none',
                    feature.accent.watermark,
                    'transition-colors duration-300 group-hover:opacity-80'
                  )}
                  aria-hidden
                >
                  {String(index + 1).padStart(2, '0')}
                </span>

                <div className="relative mb-4 flex items-center gap-3">
                  <span
                    className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-md ring-1',
                      feature.accent.iconBg,
                      feature.accent.iconText,
                      feature.accent.ring
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={2} aria-hidden />
                  </span>
                  <h3 className="font-poppins text-base font-bold text-gray-900">{feature.title}</h3>
                </div>

                <p className="relative font-inter text-sm leading-relaxed text-gray-700">
                  {feature.description}
                </p>
              </article>
            );
          })}
        </div>

        <div className="mb-12 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-5 py-4 md:px-6">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 ring-1 ring-amber-200/70">
                <Code2 className="h-4 w-4 text-amber-700" aria-hidden />
              </span>
              <div>
                <h3 className="font-poppins text-sm font-bold text-gray-900 md:text-base">
                  Exemplos de códigos
                </h3>
                <p className="font-inter text-xs text-gray-500">
                  Copie e teste no chat — a prévia visual será adicionada em breve.
                </p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-100 p-4 md:p-5">
            {ALLPOWERS_CODE_EXAMPLES.map((example) => (
              <div
                key={example.code}
                className="grid gap-4 py-4 first:pt-0 last:pb-0 md:grid-cols-[minmax(0,1fr)_140px] md:items-center md:gap-6"
              >
                <div className="min-w-0 space-y-2">
                  <p className="font-poppins text-sm font-semibold text-gray-900">{example.label}</p>
                  <CopyCodeButton code={example.code} />
                  {example.hint ? (
                    <p className="font-inter text-xs leading-relaxed text-gray-500">{example.hint}</p>
                  ) : null}
                </div>

                <div
                  className="flex h-[88px] items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50/80"
                  aria-hidden
                >
                  <span className="font-inter text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Prévia
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <AllpowersRequiredList />
      </div>
    </section>
  );
}
